<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';
import ItpIcon from '../icon/ItpIcon.vue';
import {
  registerAppsDropdownClose,
  resetAppsDropdownState,
} from './appsDropdownState';

const props = withDefaults(
  defineProps<{
    icon?: string;
    title?: string;
    closeOnRouteChange?: boolean;
  }>(),
  {
    icon: 'mdi:dots-grid',
    title: 'Apps',
    closeOnRouteChange: true,
  },
);

const isOpen = ref(false);
const rootEl = ref<HTMLLIElement | null>(null);
const toggleEl = ref<HTMLButtonElement | null>(null);

function open() {
  isOpen.value = true;
}
function close() {
  isOpen.value = false;
}
function toggle() {
  if (isOpen.value) {
    close();
  } else {
    open();
  }
  toggleEl.value?.blur();
}

watch(isOpen, (open) => {
  if (!open) {
    resetAppsDropdownState();
  }
});

function onDocumentPointerDown(e: PointerEvent) {
  if (!isOpen.value) {
    return;
  }
  const root = rootEl.value;
  const target = e.target as Node | null;
  if (root && target && root.contains(target)) {
    return;
  }
  close();
}
function onKeyDown(e: KeyboardEvent) {
  if (e.key === 'Escape' && isOpen.value) {
    close();
  }
}

onMounted(() => {
  document.addEventListener('pointerdown', onDocumentPointerDown, true);
  document.addEventListener('keydown', onKeyDown);
  registerAppsDropdownClose(close);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', onDocumentPointerDown, true);
  document.removeEventListener('keydown', onKeyDown);
  registerAppsDropdownClose(null);
});

const route = useRoute();
watch(
  () => route.fullPath,
  () => {
    if (props.closeOnRouteChange) {
      close();
    }
  },
);
</script>

<template>
  <li
    ref="rootEl"
    class="itp-apps-dropdown nav-item"
    :class="{ 'is-open': isOpen }"
  >
    <button
      ref="toggleEl"
      type="button"
      class="itp-apps-dropdown__toggle"
      :class="{ 'is-open': isOpen }"
      :title="title"
      :aria-expanded="isOpen"
      :aria-haspopup="true"
      @click="toggle"
    >
      <slot name="toggle">
        <ItpIcon :icon="icon" />
      </slot>
    </button>

    <ul
      v-show="isOpen"
      class="itp-apps-dropdown__menu dropdown-menu show"
      role="menu"
    >
      <slot />
    </ul>
  </li>
</template>

<style lang="scss">
.itp-apps-dropdown {
  position: relative;
  list-style: none;
  display: inline-flex;
  align-items: center;
  padding: 0;

  &__toggle {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    padding: 0;
    margin: 0;
    border: none;
    background: transparent;
    color: inherit;
    border-radius: 0.25rem;
    cursor: pointer;
    font-size: 1.25rem;
    line-height: 1;
    text-decoration: none;
    transition: background-color 0.12s ease;

    &:hover,
    &:focus-visible,
    &.is-open {
      background-color: rgba(0, 0, 0, 0.06);
      color: inherit !important;
      outline: none;
      text-decoration: none;
    }

    &:active {
      background-color: rgba(0, 0, 0, 0.1) !important;
      color: inherit !important;
      outline: none;
    }

    &:focus {
      outline: none;
    }
  }

  &__menu {
    position: absolute;
    top: 100%;
    left: 0;
    margin-top: 0.25rem;
    overflow: visible;
    max-height: none;
    max-width: none;
    z-index: 1050;
  }
}
</style>
